import React, { useCallback, useRef, useEffect } from 'react';
import { styled } from '@compiled/react';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import Button from '@atlaskit/button';
import AkForm, { ErrorMessage } from '@atlaskit/form';
import ClockIcon from '@atlaskit/icon/core/clock';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line import/order
import Tooltip from '@atlaskit/tooltip';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useCommonPageContext } from '@atlassian/jira-servicedesk-common-page-context';
import JsdNavigationBlocker from '@atlassian/jira-servicedesk-common/src/navigation/blocker';
import {
	MAX_SLA_GOALS_PER_SD,
	LimitData,
} from '@atlassian/jira-servicedesk-common/src/ui/components/limit-data';
import type { Intl } from '@atlassian/jira-shared-types/src/general.tsx';
import { EDIT_MODE } from '../../../../../common/constants';
import type { Metric, FormValues } from '../../../../../common/types';
import { ConditionTitle } from '../../../../../common/ui/conditions/condition-title';
import { Footer } from '../../../../../common/ui/footer/index.tsx';
import { GoalTitle } from '../../../../../common/ui/goals/goal-title';
import { RecalculationWarning } from '../../../../../common/ui/recalculation-warning-message/main.tsx';
import { RecalculationWarningInfo } from '../../../../../common/ui/recalculation-warning-message/recalculation-warning-info/index.tsx';
import { useSLAConfigActionsFlags } from '../../../../../common/ui/sla-config-action-flags';
import { SlaDisplayFormatTitle } from '../../../../../common/ui/sla-display-format/sla-display-format-title';
import { getValidationErrorMessage } from '../../../../../common/utils';
import { splitGoals } from '../../../../../common/utils/goal';
import { useSLAHierarchicalGoalsEnabled } from '../../../../../controllers/enable-sla-hierarchical-goals';
import { withFormContextProvider, useFormContext } from '../../../../../controllers/form/index.tsx';
import { useMetricActions } from '../../../../../controllers/metric-actions/index.tsx';
import { jsmSLASaveExperience } from '../../../../../experiences';
import { useRecalculationCostService } from '../../../../../services/recalculation-cost/index.tsx';
import { useUpdateMetricService } from '../../../../../services/update-metric/index.tsx';
import { Conditions } from './conditions';
import { Goals } from './goals';
import messages from './messages';
import { Name } from './name';
import { SlaDisplayFormat } from './sla-display-format';
import { useUpdateMetricResource } from './state';
import { getPayload } from './utils';

type Props = {
	isNew: boolean;
	canManageFields: boolean;
} & Intl & {
		id?: string;
		metric?: Metric;
		onCancel: () => void;
	};

const MetricFormView = ({
	id = '',
	isNew,
	// @ts-expect-error - TS2739 - Type '{}' is missing the following properties from type 'Metric': name, config
	metric = {},
	canManageFields,
	onCancel,
}: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { baseUrl, projectKey } = useCommonPageContext();

	const { formatMessage } = useIntl();

	const [, { updatePath }] = useMetricActions();
	const [{ isFormDirty }] = useFormContext();

	const [{ loading, error, data: savedMetric }, { update: updateMetric }] = useUpdateMetricService({
		// @ts-expect-error(TS2322 baseUrl, projectId or projectKey can be undefined, if usePageContext() returns null. Please handle undefined value in your code) - Type 'string | undefined' is not assignable to type 'string'.
		baseUrl,
		// @ts-expect-error(TS2322 baseUrl, projectId or projectKey can be undefined, if usePageContext() returns null. Please handle undefined value in your code) - Type 'string | undefined' is not assignable to type 'string'.
		projectKey,
	});
	const [{ updateResourceMetric }] = useUpdateMetricResource();

	const {
		name = '',
		config: { goals, definition, slaDisplayFormat },
	} = metric;

	const { defaultGoals, draggableGoals } = splitGoals(goals);
	const { timeMetricSaveSuccessFlag } = useSLAConfigActionsFlags();
	const [{ isSLAHierarchicalGoalsEnabled, isPriorityNameChanged }] =
		useSLAHierarchicalGoalsEnabled();

	const onSubmit = useCallback(
		async (formValues: FormValues) => {
			jsmSLASaveExperience.start();
			const formPayload = isNew
				? getPayload(formValues, formatMessage, isSLAHierarchicalGoalsEnabled)
				: {
						...getPayload(formValues, formatMessage, isSLAHierarchicalGoalsEnabled),
						name,
					};

			let updatedMetricData;
			try {
				updatedMetricData = await updateMetric(formPayload, id);
			} catch {
				jsmSLASaveExperience.failure();
			}

			if (
				// @ts-expect-error TS1345: An expression of type 'void' cannot be tested for truthiness.
				updatedMetricData &&
				// @ts-expect-error - TS2339 - Property 'config' does not exist on type 'never'.
				updatedMetricData.config.slaDisplayFormat &&
				// @ts-expect-error - TS2339 - Property 'config' does not exist on type 'never'.
				slaDisplayFormat !== updatedMetricData.config.slaDisplayFormat
			) {
				fireTrackAnalytics(
					createAnalyticsEvent({}),
					'jsmSlaConfiguration slaDisplayFormatChanged',
					{
						formatChange: [
							slaDisplayFormat,
							// @ts-expect-error - TS2339 - Property 'config' does not exist on type 'never'.
							updatedMetricData.config.slaDisplayFormat,
						].join(' to '),
					},
				);
			}

			if (!isNil(updatedMetricData)) {
				// @ts-expect-error - TS2345 - Argument of type 'void' is not assignable to parameter of type 'TimeMetric'.
				updateResourceMetric(updatedMetricData);
				jsmSLASaveExperience.success();
				// @ts-expect-error - TS2339 - Property 'id' does not exist on type 'void'.
				updatePath(`${updatedMetricData.id}`);
				const { name: timeMetricName } = formPayload;
				timeMetricSaveSuccessFlag(timeMetricName); // Display the success flag after successfully saving the time metric
			}
		},
		[
			isNew,
			name,
			id,
			slaDisplayFormat,
			isSLAHierarchicalGoalsEnabled,
			formatMessage,
			updateMetric,
			updateResourceMetric,
			updatePath,
			timeMetricSaveSuccessFlag,
			createAnalyticsEvent,
		],
	);

	const formRef = useRef<unknown>(null);

	const saveMetricFromBlockerModal = useCallback(() => {
		if (formRef.current !== null) {
			// https://github.com/final-form/react-final-form/blob/master/docs/faq.md#how-can-i-trigger-a-submit-from-outside-my-form
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			formRef.current.dispatchEvent(new Event('submit', { cancelable: true }));
		}
	}, []);

	const [{ data: costData, error: costError, loading: costLoading }, { fetch: fetchCost }] =
		// @ts-expect-error(TS2322 baseUrl, projectId or projectKey can be undefined, if usePageContext() returns null. Please handle undefined value in your code) - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
		useRecalculationCostService(baseUrl, projectKey);

	useEffect(() => {
		const fetchData = async () => {
			if (!costLoading && !costError && !costData) {
				await fetchCost();
			}
		};
		fetchData();
	}, [costData, costError, fetchCost, costLoading]);

	return (
		<AkForm onSubmit={onSubmit}>
			{({ formProps, getValues: getFormValues, dirty }) => (
				<>
					{!isNew && (
						<TextContainer>
							<Box xcss={iconContainerStyles}>
								<ClockIcon
									LEGACY_size="large"
									LEGACY_primaryColor={token('color.icon', colors.N500)}
									label=""
									color="currentColor"
								/>
							</Box>
							{name}
							{
								// @ts-expect-error TS-7053
								isPriorityNameChanged?.[id] ? (
									<Tooltip content={formatMessage(messages.errorTooltip)} position="top">
										<Button
											iconBefore={
												<ErrorIcon
													primaryColor={token('color.icon.danger', colors.R500)}
													size="medium"
													label={formatMessage(messages.errorTooltip)}
												/>
											}
											appearance="subtle"
										/>
									</Tooltip>
								) : null
							}
						</TextContainer>
					)}
					<form
						{...formProps}
						// @ts-expect-error - TS2322 - Type 'MutableRefObject<unknown>' is not assignable to type 'LegacyRef<HTMLFormElement> | undefined'.
						ref={formRef}
						data-test-id={`servicedesk-sla-settings.ui.metrics.metric.editable.form.metric-form-${
							id || ''
						}`}
					>
						<MetricFormViewContainer>
							{isNew && <Name name={name} canManageFields={canManageFields} />}
							<GoalTitle />
							<LimitData limitProperty={MAX_SLA_GOALS_PER_SD}>
								{(maxSlaGoalsLimit) => (
									<Goals
										defaultGoals={defaultGoals}
										draggableGoals={draggableGoals}
										maxSlaGoalsLimit={maxSlaGoalsLimit}
										getFormValues={getFormValues}
										metricId={parseInt(id, 10)}
									/>
								)}
							</LimitData>

							<ConditionTitle />
							<Conditions conditions={definition} />
							<SlaDisplayFormatTitle mode={EDIT_MODE} />
							<SlaDisplayFormat slaDisplayFormat={slaDisplayFormat} />
							<RecalculationWarning data={costData} loading={costLoading} error={costError} />
							{!loading && error && (
								<ErrorMessage data-test-id="servicedesk-sla-settings.ui.metrics.metric.editable.form.error-message">
									{getValidationErrorMessage(error) || formatMessage(messages.errorMessage)}
								</ErrorMessage>
							)}
							<FooterContainer>
								<RecalculationWarningInfo data={costData} error={costError} loading={costLoading} />
								<Footer type="submit" onCancel={onCancel} onSave={noop} isLoading={loading} />
							</FooterContainer>
						</MetricFormViewContainer>
					</form>
					{!savedMetric && (
						<JsdNavigationBlocker
							onSave={saveMetricFromBlockerModal}
							// eslint-disable-next-line @typescript-eslint/no-empty-function
							onDiscard={() => {}}
							isDirty={isFormDirty || dirty}
						/>
					)}
				</>
			)}
		</AkForm>
	);
};

export const MetricForm = withFormContextProvider(MetricFormView);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MetricFormViewContainer = styled.div({
	padding: `${token('space.200', '16px')} ${token('space.300', '24px')}`,
});

const iconContainerStyles = xcss({
	paddingRight: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FooterContainer = styled.div({
	paddingTop: token('space.100', '8px'),
	display: 'flex',
	alignItems: 'flex-end',
	justifyContent: 'flex-end',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const TextContainer = styled.div({
	font: token('font.body.large'),
	fontWeight: token('font.weight.medium'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N700),
	display: 'flex',
	alignItems: 'center',
	paddingLeft: token('space.200', '16px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${7 * gridSize}px`,
});
